import { __assign, __rest } from "tslib";
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import ReactDateInput from 'react-datepicker';
import classnames from 'classnames';
import DateInputHeader from './DatePickerHeader';
import { dateToISODateString, dateTimeToISODateString, dateToLocalTime, dateTimeToLocalTime, } from '../utilities/dateTimeUtils';
import iso8601PropType from '../propTypes/iso8601PropType';
import './DateInput.scss';
var Sizes = {
    SMALL: 'small',
    MEDIUM: 'medium'
};
var DateInputWidths = {
    SMALL: 'small',
    MEDIUM: 'medium',
    LARGE: 'large',
    FULL: 'full'
};
var TODAY = new Date();
// regex to check if time is included in the format or not
var FORMAT_REGEX = /hh|HH|h|H|k|kk|m|mm/;
var DateInput = function (_a) {
    var className = _a.className, errorMessage = _a.errorMessage, format = _a.format, inputWidth = _a.inputWidth, isDisabled = _a.isDisabled, isPinnedBottom = _a.isPinnedBottom, isPinnedRight = _a.isPinnedRight, label = _a.label, maxDate = _a.maxDate, minDate = _a.minDate, onChange = _a.onChange, placeholderText = _a.placeholderText, shouldShowFullMonthYearPicker = _a.shouldShowFullMonthYearPicker, shouldShowMonthYearPicker = _a.shouldShowMonthYearPicker, size = _a.size, timeLabel = _a.timeLabel, value = _a.value, props = __rest(_a, ["className", "errorMessage", "format", "inputWidth", "isDisabled", "isPinnedBottom", "isPinnedRight", "label", "maxDate", "minDate", "onChange", "placeholderText", "shouldShowFullMonthYearPicker", "shouldShowMonthYearPicker", "size", "timeLabel", "value"]);
    var shouldShowTimeInput = FORMAT_REGEX.test(format);
    var getLocalTime = useCallback(function (date) {
        return shouldShowTimeInput ? dateTimeToLocalTime(date) : dateToLocalTime(date);
    }, [shouldShowTimeInput]);
    // We leverage react-datepicker which expects to receive a Date object.
    // At Thrive, we prefer to use timeless ISO 8601 date strings.
    // To end up with local time on both sides and avoid the UTC conversion,
    // we parse the date string and create a new local date
    // On change, we emulate the ISO pattern but avoid the toISOString method.
    // eslint-disable-next-line no-nested-ternary
    var valueAsDateObject = value ? getLocalTime(value) : TODAY;
    var _b = useState(valueAsDateObject), selectedDate = _b[0], setSelectedDate = _b[1];
    useEffect(function () {
        /**
         * value is needed for our our util classes to work
         */
        if (value) {
            setSelectedDate(getLocalTime(value));
        }
    }, [getLocalTime, value]);
    var isSameDate = function (date1, date2, shouldCheckTime) {
        if (shouldCheckTime) {
            return date1 && date2 && date1.getTime() === date2.getTime();
        }
        return (date1 &&
            date2 &&
            date1.toLocaleDateString() === date2.toLocaleDateString());
    };
    var handleApply = function () {
        if (!selectedDate) {
            onChange(null);
        }
        else if (!value ||
            !isSameDate(valueAsDateObject, selectedDate, shouldShowTimeInput)) {
            onChange(shouldShowTimeInput
                ? dateTimeToISODateString(selectedDate)
                : dateToISODateString(selectedDate));
        }
    };
    var getDayClassNames = function (date) {
        return classnames('DateInput__day', {
            'DateInput__day--isSelected': isSameDate(date, selectedDate, false),
            'DateInput__day--isApplied': value && isSameDate(date, valueAsDateObject, false)
        });
    };
    var containerClass = classnames('DateInput', "DateInput--".concat(size), className);
    var inputClass = classnames('DateInput__input', "DateInput__input--".concat(inputWidth), { 'DateInput__input--isDisabled': isDisabled });
    var verticalPlacement = isPinnedBottom ? 'bottom' : 'top';
    var horizontalPlacement = isPinnedRight ? 'end' : 'start';
    var popperPlacement = "".concat(verticalPlacement, "-").concat(horizontalPlacement);
    return (React.createElement("div", __assign({}, props, { className: containerClass }),
        label ? React.createElement("label", { className: 'DateInput__label' }, label) : null,
        React.createElement(ReactDateInput, { adjustDateOnChange: true, calendarClassName: 'DateInput__calendar', className: inputClass, dateFormat: format, dayClassName: getDayClassNames, disabled: isDisabled, fixedHeight: true, maxDate: maxDate ? dateToLocalTime(maxDate) : null, minDate: minDate ? dateToLocalTime(minDate) : null, onCalendarClose: handleApply, onChange: function (date) {
                if (shouldShowTimeInput || !date) {
                    setSelectedDate(date);
                }
            }, onSelect: function (date, _event) {
                if (!shouldShowTimeInput) {
                    setSelectedDate(date);
                }
            }, openToDate: selectedDate, placeholderText: placeholderText, popperClassName: 'DateInput__popper', popperPlacement: popperPlacement, portalId: 'DateInput__portal', renderCustomHeader: function (_a) {
                var changeYear = _a.changeYear, date = _a.date, decreaseMonth = _a.decreaseMonth, decreaseYear = _a.decreaseYear, increaseMonth = _a.increaseMonth, increaseYear = _a.increaseYear;
                return (React.createElement(DateInputHeader, { changeYear: changeYear, date: date, onDecreaseMonth: decreaseMonth, onDecreaseYear: decreaseYear, onIncreaseMonth: increaseMonth, onIncreaseYear: increaseYear, shouldShowOnlyYear: shouldShowMonthYearPicker }));
            }, selected: value ? selectedDate : null, showFullMonthYearPicker: shouldShowFullMonthYearPicker, showMonthYearPicker: shouldShowMonthYearPicker, showPopperArrow: false, showTimeInput: shouldShowTimeInput, timeInputLabel: timeLabel, weekDayClassName: function () { return 'DateInput__weekdayName'; }, 
            // react-datepicker calls document.createRange which is not available in the JSDOM
            // environment.  By applying the withPortal prop only for tests we sidestep this issue.
            withPortal: process.env.NODE_ENV === 'test' }),
        errorMessage ? (React.createElement("div", { className: 'DateInput__errorMessage', role: 'alert' }, errorMessage)) : null));
};
DateInput.defaultProps = {
    format: 'MM/dd/yyyy',
    inputWidth: DateInputWidths.MEDIUM,
    isDisabled: false,
    isPinnedBottom: false,
    isPinnedRight: false,
    label: '',
    placeholderText: '',
    shouldShowFullMonthYearPicker: false,
    shouldShowMonthYearPicker: false,
    size: Sizes.MEDIUM,
    timeLabel: ''
};
DateInput.propTypes = {
    className: PropTypes.string,
    errorMessage: PropTypes.node,
    format: PropTypes.string,
    inputWidth: PropTypes.oneOf(Object.values(DateInputWidths)),
    isDisabled: PropTypes.bool,
    isPinnedBottom: PropTypes.bool,
    isPinnedRight: PropTypes.bool,
    label: PropTypes.string,
    maxDate: iso8601PropType,
    minDate: iso8601PropType,
    onChange: PropTypes.func.isRequired,
    placeholderText: PropTypes.string,
    shouldShowFullMonthYearPicker: PropTypes.bool,
    shouldShowMonthYearPicker: PropTypes.bool,
    size: PropTypes.oneOf(Object.values(Sizes)),
    timeLabel: PropTypes.string,
    value: iso8601PropType
};
export default DateInput;
//# sourceMappingURL=DateInput.js.map